import { z } from 'zod';
import { isValidPhoneNumber } from 'react-phone-number-input';
import dayjs from 'dayjs';

import {
  AttendeesArenaRequiredErrorText,
  CaptchaRequiredText,
  CompanyErrorText,
  CompanySegmentErrorText,
  DateOrderErrorText1,
  DateOrderErrorText2,
  EmailErrorText,
  EmailValidErrorText,
  EventEndDateError,
  EventErrorText,
  EventStartDateError,
  FirstNameErrorText,
  LastNameErrorText,
  PhoneErrorText,
  PhoneRequiredErrorText,
  PositionErrorText,
  TeamErrorText,
  TermsRequiredText,
  UploadFileDescription1,
  UploadFileDescription2,
} from '@/lib/react-intl/TranslatedTexts';

// 문의 두번째 페이지(약관 UI 보이는 곳)에서 사용하는 유효성 검사
export const zodEventType = z.object({
  business_key: z.string().nullable(),
  event_type_key: z.string().nullable(),
  termsAll: z.boolean().optional(),
  termsReq1: z.boolean().refine((value) => value === true, {
    message: <TermsRequiredText />,
  }),
  termsReq2: z.boolean().refine((value) => value === true, {
    message: <TermsRequiredText />,
  }),
});

export const zodEvent = z.object({
  first_name: z.string().min(1, { message: <FirstNameErrorText /> }),
  last_name: z.string().min(1, { message: <LastNameErrorText /> }),
  email: z
    .string()
    .min(1, { message: <EmailErrorText /> })
    .email({ message: <EmailValidErrorText /> }),
  phone: z
    .string()
    .min(1, { message: <PhoneRequiredErrorText /> })
    .refine(
      (value) => {
        return isValidPhoneNumber(value);
      },
      {
        message: <PhoneErrorText />,
      }
    ),
  company_name: z.string().min(1, { message: <CompanyErrorText /> }),
  segment: z.string().min(1, { message: <CompanySegmentErrorText /> }),
  event_name: z.string().min(1, { message: <EventErrorText /> }),
  arrival_date: z.custom((val) => val === '' || dayjs.isDayjs(val)).nullable(),
  departure_date: z.custom((val) => val === '' || dayjs.isDayjs(val)).nullable(),
  attendees: z.string().nullable(),
  rooms: z.string().nullable(),
  comments: z.string().nullable(),
  captcha: z.boolean().refine((value) => value === true, {
    message: <CaptchaRequiredText />,
  }),
});

export const zodArena = z
  .object({
    event_type: z.string().nullable(),
    first_name: z.string().min(1, { message: <FirstNameErrorText /> }),
    last_name: z.string().min(1, { message: <LastNameErrorText /> }),
    team: z.string().min(1, { message: <TeamErrorText /> }),
    position: z.string().min(1, { message: <PositionErrorText /> }),
    schedule: z.string().nullable(),
    fileList_business: z.array(z.any()).min(1, { message: <UploadFileDescription1 /> }),
    fileList_other: z.array(z.any()).min(1, { message: <UploadFileDescription2 /> }),

    email: z
      .string()
      .min(1, { message: <EmailErrorText /> })
      .email({ message: <EmailValidErrorText /> }),
    phone: z
      .string()
      .min(1, { message: <PhoneRequiredErrorText /> })
      .refine(
        (value) => {
          return isValidPhoneNumber(value);
        },
        {
          message: <PhoneErrorText />,
        }
      ),
    company_name: z.string().min(1, { message: <CompanyErrorText /> }),
    segment: z.string().min(1, { message: <CompanySegmentErrorText /> }),
    event_name: z.string().min(1, { message: <EventErrorText /> }),
    attendees: z.string().min(1, { message: <AttendeesArenaRequiredErrorText /> }),

    event_rental_start_date: z.custom((val) => dayjs.isDayjs(val), {
      message: <EventStartDateError />,
    }),
    event_rental_end_date: z.custom((val) => dayjs.isDayjs(val), {
      message: <EventEndDateError />,
    }),
    preparation_rental_start_date: z.custom((val) => val === '' || dayjs.isDayjs(val)).nullable(),
    preparation_rental_end_date: z.custom((val) => val === '' || dayjs.isDayjs(val)).nullable(),
    withdrawal_rental_start_date: z.custom((val) => val === '' || dayjs.isDayjs(val)).nullable(),
    withdrawal_rental_end_date: z.custom((val) => val === '' || dayjs.isDayjs(val)).nullable(),
    stage_type: z.string().nullable(),
    audience_type: z.string().min(1),
    comments: z.string().nullable(),
    captcha: z.boolean().refine((value) => value === true, {
      message: <CaptchaRequiredText />,
    }),
  })
  .superRefine((data, ctx) => {
    // 1. 준비대관 종료일 > 행사대관 시작일
    if (data.preparation_rental_end_date && data.event_rental_start_date) {
      const prepEnd = data.preparation_rental_end_date;
      const eventStart = data.event_rental_start_date;

      if (prepEnd.isAfter(eventStart) || prepEnd.isSame(eventStart, 'day')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['preparation_rental_end_date'],
          message: <DateOrderErrorText1 />,
        });
      }
    }

    // 2. 행사대관 종료일 >= 철수대관 시작일
    if (data.event_rental_end_date && data.withdrawal_rental_start_date) {
      const eventEnd = data.event_rental_end_date;
      const dismantleStart = data.withdrawal_rental_start_date;

      if (eventEnd.isAfter(dismantleStart)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['withdrawal_rental_start_date'],
          message: <DateOrderErrorText2 />,
        });
      }
    }
  });
